import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  distinctUntilChanged,
  map,
  merge,
  Observable,
  shareReplay,
  skip,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';

import { getCalculatedQuantity } from '@shared/utils/cargo-calculation';
import { getCargoPlaceQuantityByTrailerType } from '@shared/constants/vehicles';
import { Store } from '@ngxs/store';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import { AsyncPipe } from '@angular/common';
import {
  TuiButtonModule,
  TuiGroupModule,
  TuiHintModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { TuiInputModule, TuiInputNumberModule } from '@taiga-ui/kit';
import { CheckboxComponent } from '@shared/components/forms/checkbox/checkbox.component';
import { FormsService } from '@shared/services/forms.service';
import { roundDecimal } from '@shared/utils/math';
import { TrailerType } from '../../../../../../types/trailer';
import { OrderCargoPlaceDetailsFormWithFactPackageForm } from '../../../../../../types/cargo';
import { CargoCalculationState } from '../../../../store/cargo-calculation.state';
import { FACT_PACKAGE_QUANTITY_GREATER_THAN_ESTIMATED } from '../../validators/fact-package-quantity.validator';
import { FactPackageTypeFormComponent } from '../fact-package-type-form/fact-package-type-form.component';

@Component({
  selector: 'app-cargo-place-details',
  standalone: true,
  imports: [
    AutocompleteComponent,
    AsyncPipe,
    ReactiveFormsModule,
    TuiGroupModule,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiButtonModule,
    TuiInputNumberModule,
    FactPackageTypeFormComponent,
    TuiHintModule,
    CheckboxComponent,
  ],
  templateUrl: './cargo-place-details.component.html',
  styleUrl: './cargo-place-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CargoPlaceDetailsComponent implements OnInit {
  @Output() remove = new EventEmitter<void>();
  @Output() create = new EventEmitter<void>();

  @Input({ required: true })
  cargoPlaceDetailsForm: FormGroup<OrderCargoPlaceDetailsFormWithFactPackageForm>;

  @Input({ required: true }) isImport: boolean;
  @Input({ required: true }) last: boolean;
  hasBox$: Observable<boolean>;
  trailerType$ = this.store
    .select(CargoCalculationState.getSelectedCargo)
    .pipe(map(cargo => cargo?.trailerType));

  preparedPackageTypes$ = this.catalogs.packageTypes$.pipe(
    map(packageTypes =>
      packageTypes.map(packageType => ({
        ...packageType,
        itemText: `${packageType.code} - ${packageType.name}`,
      })),
    ),
  );

  cargoPlaceQuantityLimit = this.fb.control<Nullable<number>>({
    value: null,
    disabled: true,
  });

  protected readonly FACT_PACKAGE_QUANTITY_GREATER_THAN_ESTIMATED =
    FACT_PACKAGE_QUANTITY_GREATER_THAN_ESTIMATED;

  constructor(
    private readonly store: Store,
    protected readonly catalogs: CargoCatalogsService,
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly forms: FormsService,
  ) {}

  onCargoPlaceTypeChange() {
    this.cargoPlaceJson.controls.code.valueChanges
      .pipe(
        withLatestFrom(this.trailerType$),
        tap(([type, trailerType]) => {
          this.updateCargoPlaceDetails(type, trailerType as TrailerType);
          this.clearPackageDetails();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onPackageTypeChange() {
    this.cargoPlaceDetailsForm.controls.packageTypeJson.controls.code.valueChanges
      .pipe(
        tap(packageType => {
          this.updatePackageDetails(packageType);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onHasBoxChange() {
    this.hasBox$
      .pipe(
        skip(1),
        distinctUntilChanged(),
        tap(hasBox => {
          if (!hasBox) {
            this.cargoPlaceDetailsForm.controls.packageTypeJson.reset();
            this.cargoPlaceDetailsForm.controls.packageQuantity.reset();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onTrailerTypeChange() {
    this.trailerType$
      .pipe(
        skip(1),
        tap(trailerType => {
          const cargoPlaceType = this.cargoPlaceJson.controls.code.value ?? '';
          const packageType = this.packageTypeJson.controls.code.value ?? '';
          this.updateCargoPlaceDetails(
            cargoPlaceType,
            trailerType as TrailerType,
          );
          this.updatePackageDetails(packageType);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  clearPackageDetails() {
    this.packageTypeJson.reset();
    this.cargoPlaceDetailsForm.patchValue({
      packageQuantityLimit: null,
      packageQuantity: null,
    });
  }

  updateCargoPlaceDetails(
    cargoPlaceId: Nullable<string>,
    trailerType: TrailerType,
  ) {
    const cargoPlaceDetails = this.catalogs.cargoPlaceMap.get(
      cargoPlaceId || '',
    );
    if (cargoPlaceDetails) {
      const limit = getCargoPlaceQuantityByTrailerType(
        trailerType,
        cargoPlaceDetails.quantityInTrailerJson,
      );
      this.cargoPlaceJson.patchValue(cargoPlaceDetails, { emitEvent: false });
      this.cargoPlaceQuantityLimit.setValue(limit);
    } else {
      this.cargoPlaceJson.reset(undefined, { emitEvent: false });
      this.cargoPlaceJson.markAsDirty();
      this.cargoPlaceQuantityLimit.setValue(null);
    }
  }

  calculatePlaceQuantity() {
    merge(
      this.cargoPlaceDetailsForm.controls.packageQuantityLimit.valueChanges,
      this.cargoPlaceDetailsForm.controls.packageQuantity.valueChanges,
    )
      .pipe(
        map(() => {
          const { packageQuantityLimit = 0, packageQuantity = 0 } =
            this.cargoPlaceDetailsForm.getRawValue();
          return [packageQuantityLimit, packageQuantity];
        }),
        tap(([packageQuantityLimit, packageQuantity]) => {
          this.cargoPlaceDetailsForm.controls.cargoPlaceQuantity.setValue(
            getCalculatedQuantity(packageQuantityLimit, packageQuantity),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  updatePackageDetails(packageTypeCode: Nullable<string>) {
    const packageDetails = this.catalogs.packageTypesMap.get(
      packageTypeCode || '',
    );
    if (packageDetails) {
      this.cargoPlaceDetailsForm.controls.packageTypeJson.patchValue(
        packageDetails,
        {
          emitEvent: false,
        },
      );
    } else {
      this.cargoPlaceDetailsForm.controls.packageTypeJson.reset(
        {},
        { emitEvent: false },
      );
      this.cargoPlaceDetailsForm.controls.packageTypeJson.markAsDirty();
    }
    const packageCargoPlace = packageDetails?.cargoPlaceQuantityJson.find(
      place => place.cargoPlaceId === this.cargoPlaceJson.value.id,
    );
    this.cargoPlaceDetailsForm.controls.packageQuantityLimit.setValue(
      packageCargoPlace?.quantity ?? 0,
    );
  }

  get cargoPlaceJson() {
    return this.cargoPlaceDetailsForm.controls.cargoPlaceJson;
  }

  get packageTypeJson() {
    return this.cargoPlaceDetailsForm.controls.packageTypeJson;
  }

  setCargoPlaceLimit() {
    const cargoPlaceJson = this.cargoPlaceJson.getRawValue();
    const trailerType = this.store.selectSnapshot(
      CargoCalculationState.getSelectedCargo,
    )?.trailerType as TrailerType;
    const limit = getCargoPlaceQuantityByTrailerType(
      trailerType,
      cargoPlaceJson.quantityInTrailerJson,
    );

    this.cargoPlaceQuantityLimit.setValue(limit);
  }

  addFactPackage() {
    const group = this.forms.getFactPackageTypeForm();
    group.controls.packageTypeJson.controls.name.disable();
    this.cargoPlaceDetailsForm.controls.factPackageTypesJson.push(group);
    this.cargoPlaceDetailsForm.markAsTouched();
    this.cargoPlaceDetailsForm.markAsDirty();
    this.cdr.markForCheck();
  }

  removeFactPackage(index: number) {
    this.cargoPlaceDetailsForm.controls.factPackageTypesJson.removeAt(index);
    this.cargoPlaceDetailsForm.markAsTouched();
    this.cargoPlaceDetailsForm.markAsDirty();
    this.cdr.markForCheck();
  }

  calculateFactPlaceQuantity() {
    this.cargoPlaceDetailsForm.controls.factPackageTypesJson.valueChanges
      .pipe(
        tap(factPackageTypesJson => {
          const factCargoPlaceQuantity = factPackageTypesJson.reduce(
            (previousValue, currentValue) => {
              const { packageQuantityLimit = 0, packageQuantity = 0 } =
                currentValue;
              return (
                previousValue +
                (getCalculatedQuantity(packageQuantityLimit, packageQuantity) ??
                  0)
              );
            },
            0,
          );
          this.cargoPlaceDetailsForm.controls.factCargoPlaceQuantity.setValue(
            roundDecimal(factCargoPlaceQuantity),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.hasBox$ = this.cargoPlaceDetailsForm.controls.box.valueChanges.pipe(
      startWith(this.cargoPlaceDetailsForm.controls.box.getRawValue()),
      map(value => value ?? false),
      takeUntilDestroyed(this.destroyRef),
      shareReplay(),
    );
    this.onTrailerTypeChange();
    this.onPackageTypeChange();
    this.calculatePlaceQuantity();
    this.onHasBoxChange();
    this.onCargoPlaceTypeChange();
    this.setCargoPlaceLimit();
    this.onHasBoxChange();
    this.calculateFactPlaceQuantity();
  }
}
